import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Badge } from '@mui/material';
import { 
  Dashboard as DashboardIcon,
  Search as SearchIcon,
  SmartToy as AIIcon,
  Group as CollaborationIcon
} from '@mui/icons-material';
import { useTheme } from '../../utils/ThemeContext';
import { fetchWithAuth } from '../../utils/auth';
import { BUTTON_COLORS } from '../../config/buttonStyles';
import API_BASE_URL from '../../config/api';
import ProfileMenu from './ProfileMenu';
import TutorialsMenu from './TutorialsMenu';

const TopNavigation = () => {
  const location = useLocation();
  // eslint-disable-next-line no-unused-vars
  const [profileComplete, setProfileComplete] = useState(false);
  const [collaborationUnreadCount, setCollaborationUnreadCount] = useState(0);
  const { isDarkMode } = useTheme();

  const isActive = (path) => {
    return location.pathname === path;
  };

  const loadCollaborationUnreadCount = async () => {
    try {
      const response = await fetchWithAuth('/api/collaboration/conversation-history');
      const data = await response.json();
      
      if (data.conversations) {
        const totalUnread = data.conversations.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
        setCollaborationUnreadCount(totalUnread);
      }
    } catch (err) {
      console.error('Error loading collaboration unread count:', err);
    }
  };

  // Get profile completion status from stored user data (set during signin)
  useEffect(() => {
    const getUserProfileStatus = () => {
      try {
        // Get profile data from localStorage (set during signin)
        const userData = localStorage.getItem('userProfileData');
        if (userData) {
          const profile = JSON.parse(userData);
          // Ensure profileComplete is treated as boolean
          const isComplete = Boolean(profile.profileComplete);
          setProfileComplete(isComplete);
        } else {
          setProfileComplete(false);
        }
      } catch (error) {
        console.error('Error getting profile status:', error);
        setProfileComplete(false);
      }
    };

    // Load profile status on mount
    getUserProfileStatus();
    
    // Load collaboration unread count
    loadCollaborationUnreadCount();
    
    // Event-driven refresh for unread count
    const handleFocus = () => {
      loadCollaborationUnreadCount();
    };
    
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadCollaborationUnreadCount();
      }
    };
    
    // Custom event for immediate badge update after sending message
    const handleMessageSent = () => {
      loadCollaborationUnreadCount();
    };
    
    // Listen for profile updates
    const handleProfileUpdate = (event) => {
      if (event.detail && event.detail.profileData) {
        // Profile was updated, use the profileComplete value from the event
        const { profileData } = event.detail;
        const isComplete = Boolean(profileData.profileComplete);
        setProfileComplete(isComplete);
      }
    };
    
    // Listen for storage changes (when user signs in/out)
    const handleStorageChange = () => {
      getUserProfileStatus();
    };
    
    // Add event listeners
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('collaborationMessageSent', handleMessageSent);
    window.addEventListener('profileUpdated', handleProfileUpdate);
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('collaborationMessageSent', handleMessageSent);
      window.removeEventListener('profileUpdated', handleProfileUpdate);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []); // Empty dependency array ensures this only runs once


  return (
    <nav style={{
      background: isDarkMode 
        ? 'rgba(0, 0, 0, 0.3)' 
        : 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      borderBottom: isDarkMode 
        ? '1px solid rgba(108, 48, 130, 0.3)' 
        : '1px solid rgba(108, 48, 130, 0.2)',
      padding: '0.25rem 0',
      position: 'sticky',
      top: 0,
      zIndex: 1000,
      boxShadow: isDarkMode 
        ? '0 4px 20px rgba(0, 0, 0, 0.1)' 
        : '0 4px 20px rgba(0, 0, 0, 0.1)'
    }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        padding: '0 2rem',
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', marginRight: 'auto', marginLeft: '-10rem', marginTop: '1rem' }}>
          <img 
            src={isDarkMode ? "/aletheon-logo-white.png" : "/aletheon-logo-black.png"} 
            alt="Aletheon" 
            style={{ 
              height: '60px', 
              width: 'auto',
              transform: 'scale(1.5)',
              transformOrigin: 'left center'
            }} 
          />
        </div>

        {/* Navigation Links */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginRight: '2rem' }}>
          <Link 
            to="/dashboard" 
            className={`btn-modern ${isActive('/dashboard') ? 'nav-button-active' : ''}`}
            style={{ 
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              borderRadius: '4px',
              fontWeight: isActive('/dashboard') ? '600' : '500',
              fontSize: '0.95rem',
              transition: 'all 0.2s ease',
              background: isActive('/dashboard') ? BUTTON_COLORS.purpleGradient : (isDarkMode ? BUTTON_COLORS.grayDark : BUTTON_COLORS.grayLight),
              color: `${isActive('/dashboard') ? BUTTON_COLORS.textWhite : BUTTON_COLORS.textPrimary} !important`,
              border: isActive('/dashboard') 
                ? `1px solid ${BUTTON_COLORS.purpleBorder}` 
                : '1px solid transparent'
            }}
            onMouseEnter={(e) => {
              if (!isActive('/dashboard')) {
                e.currentTarget.style.background = BUTTON_COLORS.hoverFill;
                e.currentTarget.style.borderColor = BUTTON_COLORS.borderHover;
              } else {
                e.currentTarget.style.opacity = '0.9';
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive('/dashboard')) {
                e.currentTarget.style.background = isDarkMode ? BUTTON_COLORS.grayDark : BUTTON_COLORS.grayLight;
                e.currentTarget.style.borderColor = 'transparent';
              } else {
                e.currentTarget.style.opacity = '1';
              }
            }}
          >
            <DashboardIcon sx={{ 
              fontSize: '1.2rem', 
              color: `${isActive('/dashboard') ? BUTTON_COLORS.textWhite : BUTTON_COLORS.textPrimary} !important` 
            }} />
            <span>Dashboard</span>
          </Link>

          <Link 
            to="/homepage" 
            className={`btn-modern ${isActive('/homepage') ? 'nav-button-active' : ''}`}
            style={{ 
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              borderRadius: '4px',
              fontWeight: isActive('/homepage') ? '600' : '500',
              fontSize: '0.95rem',
              transition: 'all 0.2s ease',
              background: isActive('/homepage') ? BUTTON_COLORS.purpleGradient : (isDarkMode ? BUTTON_COLORS.grayDark : BUTTON_COLORS.grayLight),
              color: `${isActive('/homepage') ? BUTTON_COLORS.textWhite : BUTTON_COLORS.textPrimary} !important`,
              border: isActive('/homepage') 
                ? `1px solid ${BUTTON_COLORS.purpleBorder}` 
                : '1px solid transparent'
            }}
            onMouseEnter={(e) => {
              if (!isActive('/homepage')) {
                e.currentTarget.style.background = BUTTON_COLORS.hoverFill;
                e.currentTarget.style.borderColor = BUTTON_COLORS.borderHover;
              } else {
                e.currentTarget.style.opacity = '0.9';
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive('/homepage')) {
                e.currentTarget.style.background = isDarkMode ? BUTTON_COLORS.grayDark : BUTTON_COLORS.grayLight;
                e.currentTarget.style.borderColor = 'transparent';
              } else {
                e.currentTarget.style.opacity = '1';
              }
            }}
          >
            <SearchIcon sx={{ 
              fontSize: '1.2rem', 
              color: `${isActive('/homepage') ? BUTTON_COLORS.textWhite : BUTTON_COLORS.textPrimary} !important` 
            }} />
            <span>Research</span>
          </Link>

          <Link 
            to="/ai-assistant" 
            className={`btn-modern ${isActive('/ai-assistant') ? 'nav-button-active' : ''}`}
            style={{ 
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              borderRadius: '4px',
              fontWeight: isActive('/ai-assistant') ? '600' : '500',
              fontSize: '0.95rem',
              transition: 'all 0.2s ease',
              background: isActive('/ai-assistant') ? BUTTON_COLORS.purpleGradient : (isDarkMode ? BUTTON_COLORS.grayDark : BUTTON_COLORS.grayLight),
              color: `${isActive('/ai-assistant') ? BUTTON_COLORS.textWhite : BUTTON_COLORS.textPrimary} !important`,
              border: isActive('/ai-assistant') 
                ? `1px solid ${BUTTON_COLORS.purpleBorder}` 
                : '1px solid transparent'
            }}
            onMouseEnter={(e) => {
              if (!isActive('/ai-assistant')) {
                e.currentTarget.style.background = BUTTON_COLORS.hoverFill;
                e.currentTarget.style.borderColor = BUTTON_COLORS.borderHover;
              } else {
                e.currentTarget.style.opacity = '0.9';
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive('/ai-assistant')) {
                e.currentTarget.style.background = isDarkMode ? BUTTON_COLORS.grayDark : BUTTON_COLORS.grayLight;
                e.currentTarget.style.borderColor = 'transparent';
              } else {
                e.currentTarget.style.opacity = '1';
              }
            }}
          >
            <AIIcon sx={{ 
              fontSize: '1.2rem', 
              color: `${isActive('/ai-assistant') ? BUTTON_COLORS.textWhite : BUTTON_COLORS.textPrimary} !important` 
            }} />
            <span>AI Assistant</span>
          </Link>

          <Badge 
            badgeContent={collaborationUnreadCount} 
            color="error"
            sx={{
              '& .MuiBadge-badge': {
                bgcolor: 'var(--neon-cyan)',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.75rem'
              }
            }}
          >
            <Link 
              to="/collaboration" 
              className={`btn-modern ${isActive('/collaboration') ? 'nav-button-active' : ''}`}
              style={{ 
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                borderRadius: '4px',
                fontWeight: isActive('/collaboration') ? '600' : '500',
                fontSize: '0.95rem',
                transition: 'all 0.2s ease',
                background: isActive('/collaboration') ? BUTTON_COLORS.purpleGradient : (isDarkMode ? BUTTON_COLORS.grayDark : BUTTON_COLORS.grayLight),
                color: `${isActive('/collaboration') ? BUTTON_COLORS.textWhite : BUTTON_COLORS.textPrimary} !important`,
                border: isActive('/collaboration') 
                  ? `1px solid ${BUTTON_COLORS.purpleBorder}` 
                  : '1px solid transparent'
              }}
              onMouseEnter={(e) => {
                if (!isActive('/collaboration')) {
                  e.currentTarget.style.background = BUTTON_COLORS.hoverFill;
                  e.currentTarget.style.borderColor = BUTTON_COLORS.borderHover;
                } else {
                  e.currentTarget.style.opacity = '0.9';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive('/collaboration')) {
                  e.currentTarget.style.background = isDarkMode ? BUTTON_COLORS.grayDark : BUTTON_COLORS.grayLight;
                  e.currentTarget.style.borderColor = 'transparent';
                } else {
                  e.currentTarget.style.opacity = '1';
                }
              }}
            >
              <CollaborationIcon sx={{ 
                fontSize: '1.2rem', 
                color: `${isActive('/collaboration') ? BUTTON_COLORS.textWhite : BUTTON_COLORS.textPrimary} !important` 
              }} />
              <span>Collaboration</span>
            </Link>
          </Badge>
        </div>

        {/* Tutorials & User Avatar - Far Right */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <TutorialsMenu />
          <ProfileMenu />
        </div>
      </div>

    </nav>
  );
};

export default TopNavigation; 